/**
 * scoring.ts —— 决策评分（PRD 5.1）
 *
 * 阈值口径（PRD 5.1，硬编码不可改）：与最优动作的 EV 差
 *   <2% 底池 → 最优 ｜ 2%–8% → 可接受 ｜ >8% → 失误 ｜ >20% → 严重失误
 *
 * ⚠ EV 模型的简化说明（自测报告已如实标注，需 PM 确认是否满足 AC5.5 意图）：
 *   PRD 只定义了「基于精确赢率 + 底池赔率的期望值排序」和阈值，未定义对手后续反应模型。
 *   本实现按**单街、无诈唬收益、下注被跟注**的最保守模型估算 EV：
 *     fold  = 0
 *     check = equity × pot
 *     call  = equity × (pot + call) − call
 *     bet/raise（额外投入 a）= equity × (pot + 2a) − a
 *   赢率一律取引擎的**精确值**（无采样），且评分只在动作发生**之后**展示（行动前无任何建议）。
 */

import type { LegalActions } from '../core/betting';
import type { Action } from '../core/betting';
import type { ScoreToneMode } from './settings';

export type ScoreTier = 'optimal' | 'acceptable' | 'mistake' | 'blunder';

/**
 * 专业直述档评级名（沿用历史口径，保留量化直评）。复盘页徽标、专业语气 ScoreTip 用。
 */
export const TIER_NAMES_CN: Readonly<Record<ScoreTier, string>> = {
  optimal: '最优',
  acceptable: '可接受',
  mistake: '失误',
  blunder: '严重失误',
};

/**
 * 新手鼓励档评级名（需求二 FR2-2 · AC2-4）：中性 / 鼓励化措辞，
 * **不出现「严重失误」字样、不配高饱和 error 红**（配色由 ScoreTip 的 toneClass 决定）。
 */
export const TIER_NAMES_BEGINNER_CN: Readonly<Record<ScoreTier, string>> = {
  optimal: '打得好 👍',
  acceptable: '不错的选择',
  mistake: '这手可以更好 →',
  blunder: '换个思路会更好 →',
};

/** 按语气模式取评级名。 */
export function tierLabel(tier: ScoreTier, tone: ScoreToneMode): string {
  return tone === 'beginner' ? TIER_NAMES_BEGINNER_CN[tier] : TIER_NAMES_CN[tier];
}

export interface ScoreInput {
  /** 该街节点的精确 Equity（0–1）。 */
  readonly equity: number;
  /** 行动前底池（含本街其他人已投入）。 */
  readonly pot: number;
  readonly legal: LegalActions;
  readonly action: Action;
  /** 玩家本街已投入，用于把 to 语义换算成额外投入。 */
  readonly streetContributed: number;
}

export interface ScoreResult {
  readonly tier: ScoreTier;
  /** 与最优动作的 EV 差，占底池比例（0–1）。 */
  readonly evDiffRatio: number;
  readonly chosenEv: number;
  readonly bestEv: number;
  readonly bestLabel: string;
  /**
   * R4（第 10 轮）：以下字段是把 `scoreAction` 已有的评分入参**原样带出**，供解析文案使用，
   * **不新增任何计算模型、不改档位阈值**（round10-requirements R4 红线）。
   */
  /** 行动前精确赢率（0–1）。 */
  readonly equity: number;
  /** 行动前底池。 */
  readonly pot: number;
  /** 本次面对的跟注额（无需跟注时为 null）；用于底池赔率的业务语言表达。 */
  readonly callAmount: number | null;
}

function evOf(kind: 'fold' | 'check' | 'call' | 'aggro', extra: number, equity: number, pot: number): number {
  switch (kind) {
    case 'fold':
      return 0;
    case 'check':
      return equity * pot;
    case 'call':
      return equity * (pot + extra) - extra;
    case 'aggro':
      return equity * (pot + 2 * extra) - extra;
  }
}

export function scoreAction(input: ScoreInput): ScoreResult | null {
  const { equity, pot, legal, action, streetContributed } = input;
  if (pot <= 0 || !Number.isFinite(equity)) return null;

  const candidates: { label: string; ev: number }[] = [];
  if (legal.fold) candidates.push({ label: '弃牌', ev: evOf('fold', 0, equity, pot) });
  if (legal.check) candidates.push({ label: '过牌', ev: evOf('check', 0, equity, pot) });
  if (legal.call) candidates.push({ label: `跟注 ${legal.call.amount}`, ev: evOf('call', legal.call.amount, equity, pot) });
  if (legal.bet) {
    const a = Math.min(legal.bet.max, Math.max(legal.bet.min, Math.round(pot * 0.66)));
    candidates.push({ label: `下注 ${a}`, ev: evOf('aggro', a - streetContributed, equity, pot) });
  }
  if (legal.raise) {
    const a = Math.min(legal.raise.max, Math.max(legal.raise.min, Math.round(pot * 0.75)));
    candidates.push({ label: `加注至 ${a}`, ev: evOf('aggro', a - streetContributed, equity, pot) });
  }
  if (!candidates.length) return null;

  let best = candidates[0];
  for (const c of candidates) if (c.ev > best.ev) best = c;

  let chosen: number;
  switch (action.type) {
    case 'fold':
      chosen = evOf('fold', 0, equity, pot);
      break;
    case 'check':
      chosen = evOf('check', 0, equity, pot);
      break;
    case 'call':
      chosen = evOf('call', legal.call?.amount ?? 0, equity, pot);
      break;
    case 'bet':
    case 'raise':
      chosen = evOf('aggro', Math.max(0, (action.amount ?? 0) - streetContributed), equity, pot);
      break;
    case 'allin':
      chosen = evOf('aggro', legal.allIn?.chips ?? 0, equity, pot);
      break;
    default:
      return null;
  }

  const diff = Math.max(0, best.ev - chosen) / pot;
  return {
    tier: tierOf(diff),
    evDiffRatio: diff,
    chosenEv: chosen,
    bestEv: best.ev,
    bestLabel: best.label,
    // R4（第 10 轮）：把入参原样带出供解析文案使用，不新增计算
    equity,
    pot,
    callAmount: legal.call ? legal.call.amount : null,
  };
}

export function tierOf(evDiffRatio: number): ScoreTier {
  if (evDiffRatio > 0.2) return 'blunder';
  if (evDiffRatio > 0.08) return 'mistake';
  if (evDiffRatio >= 0.02) return 'acceptable';
  return 'optimal';
}

/** 一条已展示的评分记录（写入手牌历史，供复盘页事后陈述）。 */
export interface ScoreRecord {
  readonly handNumber: number;
  readonly street: string;
  readonly actionText: string;
  readonly tier: ScoreTier;
  readonly evDiffRatio: number;
  /**
   * R4（第 10 轮）：三段式失误解析所需的原始字段，全部取自 `scoreAction()` 输出，
   * **不新增计算模型、不改档位阈值**。持久化随手牌历史一起保留，复盘可复现同一段解析。
   */
  readonly equity: number;
  readonly pot: number;
  readonly callAmount: number | null;
  readonly bestLabel: string;
  /**
   * 需求二 FR2-1：本桌总席位数，用于详解模板的「多人局面（seatCount ≥ 3）走边际 / 复杂模板」
   * 保守兜底判定。取动作发生时的桌面配置（`game.config.seatCount`），随手牌历史保留、复盘可复现。
   */
  readonly seatCount: number;
}

/**
 * R4：三段式失误解析（PRD 4.3）。
 *
 * 纯字符串拼装、纯函数、可单测：把 `scoreAction()` 已产出的字段翻译成「你的动作 / 为什么错 /
 * 更优打法」三段业务语言，**不新增任何 EV 模型、不改阈值**。文案刻意避开 `format.ts` 文案红线
 * （用「更优打法」承载 `bestLabel`，不出现行动前「建议+动作」措辞）。
 */
/**
 * 需求二（FR2-1 / FR2-2）：详解按**局面置信度**分两类模板呈现，并叠加**语气**层，
 * 全部为纯字符串拼装 / 纯函数 / 可单测，**不新增 EV 模型、不改档位阈值、不改数值**。
 *
 * 模板（置信度）：
 *   - `definitive` 确定性错误（空气全下等高置信错误、单挑非面对加注的低赢率主动投入）：
 *     给客观事实 + 明确「更优打法」建议，但措辞已去方向性价值定性；
 *   - `marginal`   边际 / 复杂（边际手、多人局面 seatCount ≥ 3、面对加注的跟注 / 弃牌）：
 *     **只给客观事实**（选择 / 单挑赢率 / 底池赔率 / 需跟注额 / EV 差**区间**）+ 中性提示，
 *     **禁止**任何「领先 / 占优 / 价值加注 / 收取价值」方向性建议（AC2-1 硬红线）。
 *
 * 语气（tone）：只改评级名与配色语义（`toneClass`），**不改模板选择、不改事实与数值**（AC2-6 / AC2-8）。
 */
export type ScoreTemplate = 'definitive' | 'marginal';

export interface ScoreDetail {
  readonly tier: ScoreTier;
  readonly tone: ScoreToneMode;
  readonly template: ScoreTemplate;
  /** 标题行：`{街别} · {语气档评级名}`。 */
  readonly heading: string;
  /** 语气档评级名（徽标用）。 */
  readonly tierName: string;
  /** 一句话结论（徽标旁常驻 / hover 展开），已按语气改写。 */
  readonly oneLine: string;
  /** ① 你的选择（详解首段）。 */
  readonly actionLine: string;
  /** 详解正文段落（有序）：已按模板 + 语气产出，直接逐条渲染即可。 */
  readonly segs: readonly string[];
  /** 是否强制展开详解（mistake / blunder 为 true）。 */
  readonly threePart: boolean;
  /**
   * 配色语义类后缀（ScoreTip / 复盘用 `tone--{toneClass}`）：
   *   - `positive` 最优；`neutral` 可接受 / 全部新手档；`error` 专业档失误 / 严重失误（高饱和红）。
   * 新手语气一律不返回 `error`（AC2-4：无高饱和 error 红字）。
   */
  readonly toneClass: 'positive' | 'neutral' | 'error';
}

/** 解析入参：`ScoreRecord` 的解析相关子集（复盘与实时提示共用同一口径）。 */
export interface ScoreDetailInput {
  readonly street: string;
  readonly actionText: string;
  readonly tier: ScoreTier;
  readonly evDiffRatio: number;
  readonly equity: number;
  readonly pot: number;
  readonly callAmount: number | null;
  /** 本桌总席位数；缺省按单挑（2）处理（旧记录兜底）。 */
  readonly seatCount?: number;
  readonly bestLabel: string;
}

/** 诚实标注（AC2-9）：呈现层止血 ≠ 内核修复，如实标注赢率口径。 */
const GTO_NOTE = 'ⓘ 本评分基于单挑赢率估算，非完整 GTO 求解';
/** 边际 / 复杂模板的中性提示（AC2-2）：只提示、不给方向。 */
const MARGINAL_HINT = '提示：此局面较复杂，建议结合位置 / 对手范围判断，未必是明显错误';

/**
 * EV 差区间（FR2-1.3）：用区间弱化伪精确单值。以四舍五入的 EV 差百分点为中心给出 ±band，
 * 下限不低于 0；不引入任何新模型，只是把已有的 `evDiffRatio` 做区间化表达。
 */
function evDiffBand(evDiffRatio: number): { lo: number; hi: number } {
  const c = Math.round(evDiffRatio * 100);
  return { lo: Math.max(0, c - 2), hi: c + 3 };
}

/**
 * `definitive` 模板下的「更优打法」中性理由：
 * **刻意避开**「领先 / 占优 / 价值加注 / 收取价值」等方向性价值定性（AC2-1），只讲止损 / 控池 /
 * 底池赔率 / 建池等中性动机。
 */
function bestReason(bestLabel: string): string {
  if (bestLabel.startsWith('弃牌')) return '牌力不足以承受当前投入，弃牌止损、保留筹码。';
  if (bestLabel.startsWith('过牌')) return '赢率一般，过牌控池、不主动扩大底池。';
  if (bestLabel.startsWith('跟注')) return '按底池赔率，这里跟注在数学上是划算的。';
  if (bestLabel.startsWith('下注') || bestLabel.startsWith('加注')) return '这条街主动下注可以建立底池。';
  return '这是当前局面期望更优的选择。';
}

export function buildScoreDetail(input: ScoreDetailInput, tone: ScoreToneMode = 'pro'): ScoreDetail {
  const { street, actionText, tier, evDiffRatio, equity, pot, callAmount, bestLabel } = input;
  const seatCount = input.seatCount ?? 2;
  const eqPct = Math.round(equity * 100);
  const evDiffPct = Math.round(evDiffRatio * 100);
  const { lo, hi } = evDiffBand(evDiffRatio);
  const facingBet = callAmount !== null && callAmount > 0;
  const potOdds = facingBet ? Math.round((callAmount / (pot + callAmount)) * 100) : null;

  const isFold = actionText.startsWith('弃牌');
  const isCall = actionText.startsWith('跟注');
  const aggressive =
    actionText.startsWith('下注') || actionText.startsWith('加注') || actionText.startsWith('全下');
  const multiway = seatCount >= 3;
  const isError = tier === 'mistake' || tier === 'blunder';

  /**
   * 模板选择（FR2-1.2 保守兜底）：凡边际手、多人局面（seatCount ≥ 3）、或面对加注的跟注 / 弃牌，
   * 一律走 `marginal`（只给事实）。`definitive` 仅留给**单挑、非面对加注、主动低赢率投入**的
   * 高置信错误（空气全下 / 乱下），此时「更优打法」必为弃牌 / 过牌，措辞不含方向性价值定性。
   */
  const forcedMarginal = multiway || facingBet || isCall || isFold;
  const template: ScoreTemplate = !forcedMarginal && aggressive && isError ? 'definitive' : 'marginal';

  const factLine =
    `客观事实：1v1 赢率 ${eqPct}%` + (facingBet ? ` · 底池赔率 ${potOdds}% · 需跟注 ${callAmount}` : '');

  const actionLine = `① 你的选择：${actionText}`;
  const segs: string[] = [];

  if (!isError) {
    // 最优 / 可接受：给客观事实即可，不做「为什么错」评判。
    segs.push(factLine);
  } else if (template === 'definitive') {
    segs.push(factLine);
    segs.push(
      `为什么：赢率只有 ${eqPct}%，主动把更多筹码投进一个大概率落后的底池，比最优选择少赢约 ${lo}~${hi}% 底池。`,
    );
    segs.push(`更优打法：${bestLabel} —— ${bestReason(bestLabel)}`);
  } else {
    // 边际 / 复杂：只给客观事实 + EV 差区间 + 中性提示，禁方向建议（AC2-1 / AC2-2）。
    segs.push(factLine);
    segs.push(`评估：约少赢 ${lo}~${hi}% 底池（EV 差区间）`);
    segs.push(MARGINAL_HINT);
  }
  segs.push(GTO_NOTE);

  const beginner = tone === 'beginner';
  const oneLine =
    tier === 'optimal'
      ? beginner
        ? '打得好，这步很稳 👍'
        : '这就是当前局面的最优选择'
      : tier === 'acceptable'
        ? beginner
          ? '不错的选择，和最优很接近'
          : `接近最优，仅差 ${evDiffPct}% 底池`
        : beginner
          ? '这手可以更好，下次可以试试别的选择'
          : `这一手比最优少赢了 ${evDiffPct}% 底池`;

  const toneClass: ScoreDetail['toneClass'] =
    tier === 'optimal' ? 'positive' : beginner ? 'neutral' : isError ? 'error' : 'neutral';

  return {
    tier,
    tone,
    template,
    heading: `${street} · ${tierLabel(tier, tone)}`,
    tierName: tierLabel(tier, tone),
    oneLine,
    actionLine,
    segs,
    threePart: isError,
    toneClass,
  };
}
